import axiosInstance from './apiinterseptor';

export interface ISPOCDetails {
  name: string;
  email: string;
  phone: string;
  designation: string;
}

export interface IStatusChangeHistory {
  _id: string;
  oldStatus?: 'underProcess' | 'onboarded' | 'notOnboarded';
  newStatus: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
  updatedBy: string;
  updatedAt: string;
}

export interface CompanyReferral {
  _id: string;
  name: string;
  location: string;
  status: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
  referredBy?: {
    _id: string;
    username: string;
    email?: string;
    phone?: string;
    role?: string;
    ledby?: string;
  };
  spocDetails?: ISPOCDetails[];
  statusChangeHistory?: IStatusChangeHistory[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyReferralRequest {
  name: string;
  location: string;
  spocDetails: ISPOCDetails[];
  referredByFbdeUserId?: string;
}

export interface UpdateCompanyReferralRequest {
  name?: string;
  location?: string;
  status?: 'underProcess' | 'onboarded' | 'notOnboarded';
  spocDetails?: ISPOCDetails[];
}

export interface UpdateCompanyReferralStatusRequest {
  status: 'underProcess' | 'onboarded' | 'notOnboarded';
  remarks?: string;
}

export interface CompanyReferralListResponse {
  companyReferrals: CompanyReferral[];
  totalResults: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CompanyReferralFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'underProcess' | 'onboarded' | 'notOnboarded';
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Get company referrals with filters and pagination
 */
export const getCompanyReferrals = async (filters: CompanyReferralFilters = {}): Promise<CompanyReferralListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.page !== undefined) queryParams.append('page', filters.page.toString());
    if (filters.limit !== undefined) queryParams.append('limit', filters.limit.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

    const response = await axiosInstance.get(`/company-referrals?${queryParams.toString()}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching company referrals:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Get a company referral by ID
 */
export const getCompanyReferralById = async (id: string): Promise<CompanyReferral> => {
  try {
    const response = await axiosInstance.get(`/company-referrals/${id}`);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching company referral:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Create company referral(s) - accepts array (single or bulk)
 */
export interface BulkUploadResponse {
  success: CompanyReferral[];
  failed: Array<{ payload: CreateCompanyReferralRequest; error: string }>;
}

export const createCompanyReferral = async (
  companyReferralData: CreateCompanyReferralRequest | CreateCompanyReferralRequest[]
): Promise<CompanyReferral | BulkUploadResponse> => {
  try {
    // Always send as array
    const dataArray = Array.isArray(companyReferralData) ? companyReferralData : [companyReferralData];
    const response = await axiosInstance.post('/company-referrals', dataArray);
    
    // If single item was sent, return single object
    if (dataArray.length === 1) {
      return response.data.data;
    }
    
    // If bulk, return the bulk response structure
    return response.data.data;
  } catch (error: any) {
    console.error('Error creating company referral:', error.response?.data?.message);
    throw error;
  }
};

/**
 * Update company referral status
 */
export const updateCompanyReferralStatus = async (id: string, statusData: UpdateCompanyReferralStatusRequest): Promise<CompanyReferral> => {
  try {
    const response = await axiosInstance.patch(`/company-referrals/${id}/status`, statusData);
    return response.data.data;
  } catch (error: any) {
    console.error('Error updating company referral status:', error.response?.data?.message);
    throw error;
  }
};

